import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const TABS = ['Overview', 'Users', 'Books', 'Tokens', 'Sprint', 'CDP', 'Feedback'];

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tokenTxns, setTokenTxns] = useState([]);
  const [tokenFilter, setTokenFilter] = useState({ source: '', type: '' });
  const [sprints, setSprints] = useState([]);
  const [expandedSprint, setExpandedSprint] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState({ total: 0, pages: 1 });
  const [usersPerPage, setUsersPerPage] = useState(15);
  const [userFilter, setUserFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userSearchInput, setUserSearchInput] = useState('');
  // Feedback state
  const [feedbackItems, setFeedbackItems] = useState([]);

  // CDP state
  const [cdpDocs, setCdpDocs] = useState([]);
  const [cdpEnrolments, setCdpEnrolments] = useState([]);
  const [cdpCerts, setCdpCerts] = useState([]);
  const [cdpView, setCdpView] = useState('documents');
  const [cdpScenarios, setCdpScenarios] = useState([]);
  const [cdpSelectedDoc, setCdpSelectedDoc] = useState(null);
  const [cdpEditDoc, setCdpEditDoc] = useState(null);
  const [cdpEditScenario, setCdpEditScenario] = useState(null);
  const [cdpTemplateFile, setCdpTemplateFile] = useState(null);

  useEffect(() => {
    if (tab === 'Overview') loadStats();
    if (tab === 'Users') loadUsers(userPage);
    if (tab === 'Books') loadBooks();
    if (tab === 'Tokens') loadTokenTxns();
    if (tab === 'Sprint') loadSprints();
    if (tab === 'CDP') loadCDP();
    if (tab === 'Feedback') loadFeedback();
  }, [tab, userPage, usersPerPage, userFilter, userSearch]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    } catch (err) {
      setError('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page = userPage) => {
    setLoading(true);
    try {
      const params = `page=${page}&limit=${usersPerPage}${userFilter ? `&filter=${userFilter}` : ''}${userSearch ? `&search=${encodeURIComponent(userSearch)}` : ''}`;
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users);
      const total = data.pagination.total;
      setUserPagination({ total, pages: Math.ceil(total / usersPerPage) });
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadBooks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/books');
      setBooks(data);
    } catch {
      setError('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const loadTokenTxns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (tokenFilter.source) params.set('source', tokenFilter.source);
      if (tokenFilter.type) params.set('type', tokenFilter.type);
      const { data } = await api.get(`/admin/tokens?${params}`);
      setTokenTxns(data.txns);
    } catch {
      setError('Failed to load token transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisable = async (userId, currentlyDisabled) => {
    setError(''); setSuccess('');
    const action = currentlyDisabled ? 'enable' : 'disable';
    if (!currentlyDisabled && !window.confirm('Are you sure you want to disable this account? The user will not be able to access the platform.')) return;
    try {
      await api.put(`/admin/users/${userId}/${action}`);
      setSuccess(`Account ${action}d successfully`);
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, isDisabled: !currentlyDisabled } : u))
      );
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} account`);
    }
  };

  const handleRoleChange = async (userId, role) => {
    setError(''); setSuccess('');
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      setSuccess('Role updated');
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, role } : u))
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    }
  };

  const handleBookUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return setError('Please select a PDF file');
    setUploading(true); setError(''); setSuccess('');
    try {
      const fd = new FormData();
      fd.append('book', uploadFile);
      await api.post('/admin/book', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Book uploaded! Processing in background...');
      setUploadFile(null);
      await loadBooks();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleActivateBook = async (id) => {
    setError(''); setSuccess('');
    try {
      await api.put(`/admin/book/${id}/activate`);
      setSuccess('Book activated as the active knowledge base');
      await loadBooks();
    } catch (err) {
      setError(err.response?.data?.error || 'Activation failed');
    }
  };

  const handleDeactivateBook = async (id) => {
    setError(''); setSuccess('');
    try {
      await api.put(`/admin/book/${id}/deactivate`);
      setSuccess('Book deactivated');
      await loadBooks();
    } catch (err) {
      setError(err.response?.data?.error || 'Deactivation failed');
    }
  };

  const handleDeleteBook = async (id, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?\n\nThis will remove the file from storage and all its embeddings from the database. This cannot be undone.`)) return;
    setError(''); setSuccess('');
    try {
      await api.delete(`/admin/book/${id}`);
      setSuccess('Book deleted from server and database');
      await loadBooks();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const loadSprints = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sprint/admin/list');
      setSprints(data.sprints);
    } catch {
      setError('Failed to load sprints');
    } finally {
      setLoading(false);
    }
  };

  const handleSprintReview = async (sprintId, task, decision) => {
    setError(''); setSuccess('');
    try {
      await api.put(`/sprint/admin/${sprintId}/${task}/review`, { decision, note: reviewNote });
      setSuccess(`Task ${task === 'task1' ? '1' : '2'} marked as ${decision}`);
      setReviewNote('');
      await loadSprints();
    } catch (err) {
      setError(err.response?.data?.error || 'Review failed');
    }
  };

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/feedback/admin/all');
      setFeedbackItems(data);
    } catch { setError('Failed to load feedback'); }
    setLoading(false);
  };

  const loadCDP = async () => {
    setLoading(true);
    try {
      const [d, e, c] = await Promise.all([
        api.get('/cdp/admin/documents'),
        api.get('/cdp/admin/enrolments'),
        api.get('/cdp/admin/certificates'),
      ]);
      setCdpDocs(d.data); setCdpEnrolments(e.data); setCdpCerts(c.data);
    } catch { setError('Failed to load CDP'); }
    finally { setLoading(false); }
  };

  const loadCDPScenarios = async (docId) => {
    try {
      const { data } = await api.get(`/cdp/admin/scenarios?documentId=${docId}`);
      setCdpScenarios(data);
    } catch { setError('Failed to load scenarios'); }
  };

  const handleCdpSaveDoc = async (docData) => {
    try {
      if (docData._id) {
        await api.put(`/cdp/admin/documents/${docData._id}`, docData);
      } else {
        await api.post('/cdp/admin/documents', docData);
      }
      setCdpEditDoc(null);
      loadCDP();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save document'); }
  };

  const handleCdpUploadTemplate = async (docId) => {
    if (!cdpTemplateFile) return;
    try {
      const fd = new FormData(); fd.append('file', cdpTemplateFile);
      await api.post(`/cdp/admin/documents/${docId}/upload-template`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCdpTemplateFile(null);
      setSuccess('Template uploaded');
      loadCDP();
    } catch (err) { setError(err.response?.data?.error || 'Upload failed'); }
  };

  const handleCdpSaveScenario = async (scData) => {
    try {
      if (scData._id) {
        await api.put(`/cdp/admin/scenarios/${scData._id}`, scData);
      } else {
        await api.post('/cdp/admin/scenarios', scData);
      }
      setCdpEditScenario(null);
      if (cdpSelectedDoc) loadCDPScenarios(cdpSelectedDoc);
    } catch (err) { setError(err.response?.data?.error || 'Failed to save scenario'); }
  };

  const sprintStatusLabel = (status) => {
    const map = { not_started: 'Not Started', in_progress: 'In Progress', activities_selected: 'Activities Selected', submitted: 'Submitted', under_review: 'Under Review', approved: 'Approved', needs_revision: 'Needs Revision' };
    return map[status] || status;
  };

  const sprintStatusColor = (status) => {
    if (status === 'approved') return { background: '#d1fae5', color: '#065f46' };
    if (status === 'submitted') return { background: '#fef3c7', color: '#92400e' };
    if (status === 'needs_revision') return { background: '#fee2e2', color: '#991b1b' };
    if (status === 'in_progress' || status === 'activities_selected') return { background: '#dbeafe', color: '#1e40af' };
    return { background: 'var(--border-2)', color: 'var(--text-3)' };
  };

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1 style={{ ...styles.title, marginBottom: 0 }}>⚙️ Admin Panel</h1>
        <button onClick={() => navigate('/admin/chat')} style={styles.chatDashBtn}>
          💬 Direct Chats
        </button>
      </div>

      <div style={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setError(''); setSuccess(''); }}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
          >
            {t}
          </button>
        ))}
      </div>

      {error && <p style={styles.error}>{error}</p>}
      {success && <p style={styles.success}>{success}</p>}

      {loading && <p style={styles.loading}>Loading...</p>}

      {/* Overview */}
      {tab === 'Overview' && stats && (
        <div style={styles.statsGrid}>
          <StatCard icon="👥" label="Total Users" value={stats.totalUsers} color="#3182ce" onClick={() => { setUserFilter(''); setUserPage(1); setTab('Users'); }} />
          <StatCard icon="💬" label="Total Chats" value={stats.totalChats} color="#38a169" onClick={() => { setUserFilter('has_chats'); setUserPage(1); setTab('Users'); }} />
          <StatCard icon="📄" label="Total Resumes" value={stats.totalResumes} color="#d69e2e" onClick={() => { setUserFilter('has_resumes'); setUserPage(1); setTab('Users'); }} />
          <StatCard icon="🔥" label="Chats (7 days)" value={stats.recentChats} color="#e53e3e" onClick={() => { setUserFilter('recent_chats'); setUserPage(1); setTab('Users'); }} />
          <StatCard icon="🧭" label="Assessments" value={stats.totalAssessments} color="#7c3aed" onClick={() => { setUserFilter('assessment'); setUserPage(1); setTab('Users'); }} />
          <StatCard icon="⚡" label="Total Sprints" value={stats.totalSprints} color="#0891b2" onClick={() => { setTab('Sprint'); }} />
          <StatCard icon="🏃" label="Active Sprints" value={stats.activeSprints} color="#059669" onClick={() => { setTab('Sprint'); }} />
          <StatCard icon="🎤" label="Mock Interviews" value={stats.totalMockInterviews} color="#7c3aed" onClick={() => { setUserFilter('mock_interview'); setUserPage(1); setTab('Users'); }} />
          <StatCard icon="👤" label="Interview Users" value={stats.mockInterviewUsers} color="#6d28d9" onClick={() => { setUserFilter('mock_interview'); setUserPage(1); setTab('Users'); }} />
          <div style={{ ...styles.statCard, gridColumn: '1 / -1', background: '#f0fff4', flexDirection: 'column', alignItems: 'flex-start' }}>
            <p style={styles.statLabel}>Active Books ({stats.activeBooks?.length || 0})</p>
            {stats.activeBooks?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                {stats.activeBooks.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <p style={{ fontWeight: '700', color: '#276749', fontSize: '14px' }}>{b.name}</p>
                    <p style={{ color: '#718096', fontSize: '12px' }}>{b.chunks} chunks</p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#a0aec0' }}>No active books. Upload one in the Books tab.</p>
            )}
          </div>
        </div>
      )}

      {/* Users */}
      {tab === 'Users' && !loading && (
        <div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', alignItems: 'center' }}>
          <input
            value={userSearchInput}
            onChange={e => setUserSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setUserSearch(userSearchInput); setUserPage(1); } }}
            placeholder="Search by name, email or phone..."
            style={{ ...styles.input, maxWidth: '320px' }}
          />
          <button onClick={() => { setUserSearch(userSearchInput); setUserPage(1); }} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>Search</button>
          {userSearch && (
            <button onClick={() => { setUserSearch(''); setUserSearchInput(''); setUserPage(1); }} style={{ background: '#fff', border: '1px solid var(--border)', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: 'var(--text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>Clear</button>
          )}
        </div>
        {userFilter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', padding: '10px 16px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '10px' }}>
            <span style={{ fontSize: '13px', color: '#4338ca', fontWeight: '600' }}>
              Filtered: {
                { has_chats: 'Users with chats', has_resumes: 'Users with resumes', recent_chats: 'Active in last 7 days', assessment: 'Completed assessment', sprint: 'Enrolled in Sprint', sprint_active: 'Active Sprint' }[userFilter] || userFilter
              }
            </span>
            <button
              onClick={() => { setUserFilter(''); setUserPage(1); }}
              style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', color: '#4338ca', cursor: 'pointer', marginLeft: 'auto' }}
            >
              Clear Filter
            </button>
          </div>
        )}
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name / Phone', 'Email', 'Role', 'Status', 'CDP', 'Interview', 'Queries', 'Assessment', 'Sprint', 'Joined'].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u._id} style={styles.tr}>
                  <td style={styles.td}>
                    <p style={{ fontWeight: '600', color: '#1a365d', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate(`/admin/users/${u._id}`)}>{u.name || '-'}</p>
                    <a href={`https://wa.me/${u.phone?.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#718096', textDecoration: 'underline' }}>{u.phone}</a>
                  </td>
                  <td style={styles.td}>{u.email || '-'}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...roleBadgeColor(u.role) }}>{u.role}</span>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleToggleDisable(u._id, u.isDisabled)}
                      style={u.isDisabled ? styles.enableBtn : styles.disableBtn}
                    >
                      {u.isDisabled ? 'Enable' : 'Disable'}
                    </button>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={async () => { await api.put(`/cdp/admin/user/${u._id}/access`); loadUsers(userPage); }}
                      style={{ ...styles.badge, cursor: 'pointer', border: 'none', ...(u.cdpAccess ? { background: '#d1fae5', color: '#065f46' } : { background: '#f3f4f6', color: '#9ca3af' }) }}
                    >
                      {u.cdpAccess ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={async () => { await api.put(`/mock-interview/admin/user/${u._id}/access`); loadUsers(userPage); }}
                      style={{ ...styles.badge, cursor: 'pointer', border: 'none', ...(u.interviewAccess ? { background: '#dbeafe', color: '#1e40af' } : { background: '#f3f4f6', color: '#9ca3af' }) }}
                    >
                      {u.interviewAccess ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontWeight: '700', color: '#1a365d' }}>{(u.totalQueries || 0).toLocaleString()}</span>
                  </td>
                  <td style={styles.td}>
                    {u.assessmentArea ? (
                      <span style={{ ...styles.badge, background: '#ede9fe', color: '#6d28d9' }}>{u.assessmentArea}</span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#a0aec0' }}>–</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {u.sprintStatus ? (
                      <span style={{ ...styles.badge, ...(u.sprintStatus === 'completed' ? { background: '#d1fae5', color: '#065f46' } : { background: '#dbeafe', color: '#1e40af' }) }}>
                        {u.sprintStatus === 'completed' ? 'Completed' : 'Active'}
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#a0aec0' }}>–</span>
                    )}
                  </td>
                  <td style={styles.td}>{new Date(u.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {userPagination.pages > 1 && (
          <div style={styles.pagination}>
            <button
              onClick={() => setUserPage((p) => Math.max(1, p - 1))}
              disabled={userPage <= 1}
              style={{ ...styles.pageBtn, opacity: userPage <= 1 ? 0.4 : 1 }}
            >
              ← Prev
            </button>
            <div style={styles.pageNumbers}>
              {Array.from({ length: userPagination.pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setUserPage(p)}
                  style={{
                    ...styles.pageNumBtn,
                    ...(p === userPage ? styles.pageNumActive : {}),
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              onClick={() => setUserPage((p) => Math.min(userPagination.pages, p + 1))}
              disabled={userPage >= userPagination.pages}
              style={{ ...styles.pageBtn, opacity: userPage >= userPagination.pages ? 0.4 : 1 }}
            >
              Next →
            </button>
            <span style={styles.pageInfo}>
              {userPagination.total} users
            </span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: userPagination.pages > 1 ? '0' : '12px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>Show</span>
          <select
            value={usersPerPage}
            onChange={(e) => { setUsersPerPage(Number(e.target.value)); setUserPage(1); }}
            style={styles.select}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>per page</span>
        </div>
        </div>
      )}

      {/* Tokens */}
      {tab === 'Tokens' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={tokenFilter.source}
              onChange={(e) => setTokenFilter((f) => ({ ...f, source: e.target.value }))}
              style={styles.select}
            >
              <option value="">All sources</option>
              <option value="admin_grant">Admin grant</option>
              <option value="purchase">Purchase</option>
              <option value="chat_usage">Chat usage</option>
            </select>
            <select
              value={tokenFilter.type}
              onChange={(e) => setTokenFilter((f) => ({ ...f, type: e.target.value }))}
              style={styles.select}
            >
              <option value="">Credits &amp; Debits</option>
              <option value="credit">Credits only</option>
              <option value="debit">Debits only</option>
            </select>
            <button onClick={loadTokenTxns} style={{ ...styles.activateBtn, marginLeft: 'auto' }}>Refresh</button>
          </div>
          {loading ? <p style={styles.loading}>Loading...</p> : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Date', 'User', 'Type', 'Source', 'Amount', 'Balance After', 'Added By', 'Note'].map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tokenTxns.map((t) => (
                    <tr key={t._id} style={styles.tr}>
                      <td style={styles.td}>{new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={styles.td}>
                        <p style={{ fontWeight: '600', color: '#1a365d', fontSize: '12px' }}>{t.userId?.name || '-'}</p>
                        <p style={{ fontSize: '11px', color: '#718096' }}>{t.userId?.phone}</p>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...(t.type === 'credit' ? { background: '#c6f6d5', color: '#276749' } : { background: '#fed7d7', color: '#c53030' }) }}>
                          {t.type === 'credit' ? '+ Credit' : '− Debit'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontSize: '12px', color: '#4a5568' }}>
                          {t.source === 'admin_grant' && 'Admin grant'}
                          {t.source === 'purchase' && 'Purchase'}
                          {t.source === 'chat_usage' && 'Chat usage'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: '800', color: t.type === 'credit' ? '#276749' : '#c53030' }}>
                          {t.type === 'credit' ? '+' : '−'}{t.amount.toLocaleString()}
                        </span>
                      </td>
                      <td style={styles.td}>{t.balanceAfter.toLocaleString()}</td>
                      <td style={styles.td}>{t.addedBy ? (t.addedBy.name || t.addedBy.phone) : '-'}</td>
                      <td style={{ ...styles.td, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.note || '-'}</td>
                    </tr>
                  ))}
                  {tokenTxns.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No token transactions found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Books */}
      {tab === 'Books' && !loading && (
        <div>
          {/* Upload form */}
          <div style={styles.uploadCard}>
            <h3 style={styles.cardTitle}>Upload New Book (PDF)</h3>
            <form onSubmit={handleBookUpload} style={styles.uploadForm}>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setUploadFile(e.target.files[0])}
                style={styles.fileInput}
              />
              <button type="submit" disabled={uploading || !uploadFile} style={styles.uploadBtn}>
                {uploading ? 'Uploading...' : 'Upload & Process'}
              </button>
            </form>
            <p style={styles.hint}>
              The book will be uploaded to Firebase Storage, text extracted, split into ~500-token chunks, embedded using OpenAI, and stored in MongoDB for vector search.
            </p>
          </div>

          {/* Books list */}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['File Name', 'Status', 'Chunks', 'Uploaded', 'Active', 'Action'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {books.map((b) => (
                  <tr key={b._id} style={styles.tr}>
                    <td style={styles.td}>{b.fileName}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...statusBadge(b.status) }}>{b.status}</span>
                    </td>
                    <td style={styles.td}>{b.totalChunks}</td>
                    <td style={styles.td}>{new Date(b.uploadedAt).toLocaleDateString()}</td>
                    <td style={styles.td}>{b.isActive ? '✅' : '-'}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {b.status === 'ready' && !b.isActive && (
                          <button onClick={() => handleActivateBook(b._id)} style={styles.activateBtn}>
                            Set Active
                          </button>
                        )}
                        {b.isActive && (
                          <button onClick={() => handleDeactivateBook(b._id)} style={styles.deactivateBtn}>
                            Deactivate
                          </button>
                        )}
                        <button onClick={() => handleDeleteBook(b._id, b.fileName)} style={styles.deleteBtn}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {books.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>
                      No books uploaded yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sprint */}
      {tab === 'Sprint' && !loading && (
        <div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Student', 'College', 'Year', 'Start Date', 'Task 1', 'Task 2', 'Days Left (T1/T2)', ''].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sprints.map((sp) => {
                  const user = sp.userId || {};
                  const t1d = Math.max(0, Math.ceil((new Date(sp.task1Deadline) - new Date()) / 86400000));
                  const t2d = Math.max(0, Math.ceil((new Date(sp.task2Deadline) - new Date()) / 86400000));
                  const isExpanded = expandedSprint === sp._id;
                  return (
                    <React.Fragment key={sp._id}>
                      <tr style={styles.tr}>
                        <td style={styles.td}>
                          <p style={{ fontWeight: '600', color: '#1a365d' }}>{user.name || '-'}</p>
                          <p style={{ fontSize: '11px', color: '#718096' }}>{user.phone}</p>
                        </td>
                        <td style={styles.td}>{user.college || '-'}</td>
                        <td style={styles.td}>{user.year || '-'}</td>
                        <td style={styles.td}>{new Date(sp.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, ...sprintStatusColor(sp.task1?.status) }}>{sprintStatusLabel(sp.task1?.status)}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, ...sprintStatusColor(sp.task2?.status) }}>{sprintStatusLabel(sp.task2?.status)}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: '600' }}>{t1d} / {t2d}</span>
                        </td>
                        <td style={styles.td}>
                          <button
                            onClick={() => setExpandedSprint(isExpanded ? null : sp._id)}
                            style={styles.viewBtn}
                          >
                            {isExpanded ? 'Close' : 'Review'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} style={{ padding: '16px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                            {/* Task 1 Review */}
                            <div style={{ marginBottom: '20px' }}>
                              <h4 style={{ color: 'var(--navy)', marginBottom: '8px', fontSize: '14px' }}>Task 1: Know Your Law School</h4>
                              {sp.task1?.fileName ? (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '13px' }}>File: <strong>{sp.task1.fileName}</strong></span>
                                  {sp.task1.fileUrl && (
                                    <a href={sp.task1.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)' }}>Download</a>
                                  )}
                                </div>
                              ) : (
                                <p style={{ fontSize: '13px', color: 'var(--text-3)' }}>No file uploaded yet.</p>
                              )}
                              {sp.task1?.noteForNitin && (
                                <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '8px' }}>Note: <em>{sp.task1.noteForNitin}</em></p>
                              )}
                              {sp.task1?.reviewNote && (
                                <p style={{ fontSize: '12px', color: 'var(--danger)', marginBottom: '8px' }}>Previous review note: {sp.task1.reviewNote}</p>
                              )}
                              {sp.task1?.status === 'submitted' && (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <input value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Optional note" style={{ ...styles.select, flex: '1', minWidth: '200px', padding: '6px 10px' }} />
                                  <button onClick={() => handleSprintReview(sp._id, 'task1', 'approved')} style={styles.activateBtn}>Approve</button>
                                  <button onClick={() => handleSprintReview(sp._id, 'task1', 'needs_revision')} style={styles.deactivateBtn}>Needs Revision</button>
                                </div>
                              )}
                            </div>

                            {/* Task 2 Review */}
                            <div>
                              <h4 style={{ color: 'var(--navy)', marginBottom: '8px', fontSize: '14px' }}>Task 2: Building Your Professional Foundation</h4>
                              {sp.task2?.responses?.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
                                  {sp.task2.responses.map((resp, idx) => (
                                    <div key={idx} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                                      <p style={{ fontWeight: '600', color: 'var(--navy)', fontSize: '13px', marginBottom: '6px' }}>Activity {resp.activityIndex + 1}</p>
                                      <p style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '4px' }}><strong>What did you do:</strong> {resp.whatDidYouDo || '-'}</p>
                                      <p style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '4px' }}><strong>What did you learn:</strong> {resp.whatDidYouLearn || '-'}</p>
                                      <p style={{ fontSize: '12px', color: 'var(--text-2)' }}><strong>What will you do next:</strong> {resp.whatWillYouDoNext || '-'}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p style={{ fontSize: '13px', color: 'var(--text-3)', marginBottom: '8px' }}>No responses submitted yet.</p>
                              )}
                              {sp.task2?.reviewNote && (
                                <p style={{ fontSize: '12px', color: 'var(--danger)', marginBottom: '8px' }}>Previous review note: {sp.task2.reviewNote}</p>
                              )}
                              {sp.task2?.status === 'submitted' && (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <input value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Optional note" style={{ ...styles.select, flex: '1', minWidth: '200px', padding: '6px 10px' }} />
                                  <button onClick={() => handleSprintReview(sp._id, 'task2', 'approved')} style={styles.activateBtn}>Approve</button>
                                  <button onClick={() => handleSprintReview(sp._id, 'task2', 'needs_revision')} style={styles.deactivateBtn}>Needs Revision</button>
                                </div>
                              )}
                            </div>

                            {sp.overallStatus === 'completed' && (
                              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#d1fae5', borderRadius: '8px', color: '#065f46', fontSize: '13px', fontWeight: '600' }}>
                                Both tasks approved. Student can schedule Zoom session.
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {sprints.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No Sprint students yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CDP Tab */}
      {tab === 'CDP' && !loading && (
        <div>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', borderBottom: '2px solid var(--border)' }}>
            {['documents', 'scenarios', 'enrolments', 'certificates'].map(v => (
              <button key={v} onClick={() => { setCdpView(v); if (v === 'scenarios' && cdpDocs.length > 0 && !cdpSelectedDoc) { setCdpSelectedDoc(cdpDocs[0]._id); loadCDPScenarios(cdpDocs[0]._id); } }} style={{ ...styles.tab, ...(cdpView === v ? styles.tabActive : {}), textTransform: 'capitalize' }}>{v}</button>
            ))}
          </div>

          {/* Documents view */}
          {cdpView === 'documents' && (
            <div>
              <button onClick={() => setCdpEditDoc({ order: cdpDocs.length + 1, title: '', shortTitle: '', description: '' })} style={{ ...styles.badge, cursor: 'pointer', border: 'none', background: '#0c2340', color: '#fff', marginBottom: '10px', padding: '6px 14px', fontSize: '12px' }}>+ Add Document</button>
              {cdpEditDoc && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700' }}>{cdpEditDoc._id ? 'Edit' : 'New'} Document</h4>
                  <div style={{ display: 'grid', gap: '8px', gridTemplateColumns: '60px 1fr 1fr' }}>
                    <input placeholder="Order" type="number" value={cdpEditDoc.order || ''} onChange={e => setCdpEditDoc({ ...cdpEditDoc, order: Number(e.target.value) })} style={styles.input} />
                    <input placeholder="Title" value={cdpEditDoc.title || ''} onChange={e => setCdpEditDoc({ ...cdpEditDoc, title: e.target.value })} style={styles.input} />
                    <input placeholder="Short Title" value={cdpEditDoc.shortTitle || ''} onChange={e => setCdpEditDoc({ ...cdpEditDoc, shortTitle: e.target.value })} style={styles.input} />
                  </div>
                  <textarea placeholder="Description" value={cdpEditDoc.description || ''} onChange={e => setCdpEditDoc({ ...cdpEditDoc, description: e.target.value })} style={{ ...styles.input, marginTop: '6px', minHeight: '60px', resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    <button onClick={() => handleCdpSaveDoc(cdpEditDoc)} style={{ ...styles.badge, cursor: 'pointer', border: 'none', background: '#065f46', color: '#fff', padding: '5px 14px' }}>Save</button>
                    <button onClick={() => setCdpEditDoc(null)} style={{ ...styles.badge, cursor: 'pointer', border: 'none', background: '#6b7280', color: '#fff', padding: '5px 14px' }}>Cancel</button>
                  </div>
                </div>
              )}
              <div style={styles.tableWrap}><table style={styles.table}>
                <thead><tr>{['#', 'Title', 'Short', 'Scenarios', 'Template', 'Published', 'Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {cdpDocs.map(d => (
                    <tr key={d._id} style={styles.tr}>
                      <td style={styles.td}>{d.order}</td>
                      <td style={styles.td}><strong>{d.title}</strong></td>
                      <td style={styles.td}>{d.shortTitle || '—'}</td>
                      <td style={styles.td}>{d.scenarioCount || 0}</td>
                      <td style={styles.td}>
                        {d.templateFileUrl ? (
                          <a href={d.templateFileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', fontWeight: '700', color: '#0c2340' }}>Download</a>
                        ) : (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input type="file" accept=".docx,.doc" onChange={e => setCdpTemplateFile(e.target.files[0])} style={{ fontSize: '10px', maxWidth: '120px' }} />
                            <button onClick={() => handleCdpUploadTemplate(d._id)} style={{ fontSize: '10px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff' }}>Upload</button>
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <button onClick={async () => { await api.put(`/cdp/admin/documents/${d._id}/publish`); loadCDP(); }} style={{ ...styles.badge, cursor: 'pointer', border: 'none', ...(d.isPublished ? { background: '#d1fae5', color: '#065f46' } : { background: '#fee2e2', color: '#991b1b' }) }}>
                          {d.isPublished ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => setCdpEditDoc(d)} style={{ fontSize: '10px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff' }}>Edit</button>
                          <button onClick={async () => { if (window.confirm(`Delete "${d.title}"?`)) { await api.delete(`/cdp/admin/documents/${d._id}`); loadCDP(); } }} style={styles.deleteBtn}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {cdpDocs.length === 0 && <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No documents. Run: node backend/seed/seedCDP.js</td></tr>}
                </tbody>
              </table></div>
            </div>
          )}

          {/* Scenarios view */}
          {cdpView === 'scenarios' && (
            <div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                <select value={cdpSelectedDoc || ''} onChange={e => { setCdpSelectedDoc(e.target.value); loadCDPScenarios(e.target.value); }} style={{ ...styles.input, maxWidth: '280px' }}>
                  {cdpDocs.map(d => <option key={d._id} value={d._id}>{d.order}. {d.title}</option>)}
                </select>
                <button onClick={() => setCdpEditScenario({ documentId: cdpSelectedDoc, code: '', title: '', briefText: '' })} style={{ ...styles.badge, cursor: 'pointer', border: 'none', background: '#0c2340', color: '#fff', padding: '6px 14px', fontSize: '12px' }}>+ Add Scenario</button>
              </div>
              {cdpEditScenario && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700' }}>{cdpEditScenario._id ? 'Edit' : 'New'} Scenario</h4>
                  <div style={{ display: 'grid', gap: '8px', gridTemplateColumns: '80px 1fr' }}>
                    <input placeholder="Code (e.g. 1A)" value={cdpEditScenario.code || ''} onChange={e => setCdpEditScenario({ ...cdpEditScenario, code: e.target.value })} style={styles.input} />
                    <input placeholder="Title" value={cdpEditScenario.title || ''} onChange={e => setCdpEditScenario({ ...cdpEditScenario, title: e.target.value })} style={styles.input} />
                  </div>
                  <textarea placeholder="Brief text (the full scenario)" value={cdpEditScenario.briefText || ''} onChange={e => setCdpEditScenario({ ...cdpEditScenario, briefText: e.target.value })} style={{ ...styles.input, marginTop: '6px', minHeight: '120px', resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    <button onClick={() => handleCdpSaveScenario(cdpEditScenario)} style={{ ...styles.badge, cursor: 'pointer', border: 'none', background: '#065f46', color: '#fff', padding: '5px 14px' }}>Save</button>
                    <button onClick={() => setCdpEditScenario(null)} style={{ ...styles.badge, cursor: 'pointer', border: 'none', background: '#6b7280', color: '#fff', padding: '5px 14px' }}>Cancel</button>
                  </div>
                </div>
              )}
              <div style={styles.tableWrap}><table style={styles.table}>
                <thead><tr>{['Code', 'Title', 'Brief (preview)', 'Active', 'Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {cdpScenarios.map(sc => (
                    <tr key={sc._id} style={styles.tr}>
                      <td style={styles.td}><strong>{sc.code}</strong></td>
                      <td style={styles.td}>{sc.title}</td>
                      <td style={{ ...styles.td, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sc.briefText?.slice(0, 100)}...</td>
                      <td style={styles.td}>
                        <button onClick={async () => { await api.put(`/cdp/admin/scenarios/${sc._id}`, { isActive: !sc.isActive }); loadCDPScenarios(cdpSelectedDoc); }} style={{ ...styles.badge, cursor: 'pointer', border: 'none', ...(sc.isActive ? { background: '#d1fae5', color: '#065f46' } : { background: '#fee2e2', color: '#991b1b' }) }}>
                          {sc.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => setCdpEditScenario(sc)} style={{ fontSize: '10px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff' }}>Edit</button>
                          <button onClick={async () => { if (window.confirm('Delete this scenario?')) { await api.delete(`/cdp/admin/scenarios/${sc._id}`); loadCDPScenarios(cdpSelectedDoc); } }} style={styles.deleteBtn}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {cdpScenarios.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No scenarios for this document</td></tr>}
                </tbody>
              </table></div>
            </div>
          )}

          {/* Enrolments view */}
          {cdpView === 'enrolments' && (
            <div style={styles.tableWrap}><table style={styles.table}>
              <thead><tr>{['Student', 'Document', 'Scenario', 'Attempt', 'Status', 'Score', 'Date'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
              <tbody>
                {cdpEnrolments.map(en => (
                  <tr key={en._id} style={styles.tr}>
                    <td style={styles.td}><span style={{ fontWeight: '600', color: '#1a365d', cursor: 'pointer', textDecoration: 'underline', fontSize: '12px' }} onClick={() => window.open(`/admin/users/${en.userId?._id}`, '_blank')}>{en.userId?.name || en.userId?.phone || '-'}</span></td>
                    <td style={styles.td}>{en.documentId?.title || '-'}</td>
                    <td style={styles.td}>{en.scenarioId?.code || '-'}</td>
                    <td style={styles.td}>{en.attemptNumber}</td>
                    <td style={styles.td}><span style={{ ...styles.badge, ...(en.status === 'passed' ? { background: '#d1fae5', color: '#065f46' } : en.status === 'resubmit' ? { background: '#fee2e2', color: '#991b1b' } : { background: '#dbeafe', color: '#1e40af' }) }}>{en.status?.replace(/_/g, ' ')}</span></td>
                    <td style={styles.td}>{en.assessment?.totalScore != null ? <strong>{en.assessment.totalScore}/100</strong> : '—'}</td>
                    <td style={styles.td}>{new Date(en.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</td>
                  </tr>
                ))}
                {cdpEnrolments.length === 0 && <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No enrolments yet</td></tr>}
              </tbody>
            </table></div>
          )}

          {/* Certificates view */}
          {cdpView === 'certificates' && (
            <div style={styles.tableWrap}><table style={styles.table}>
              <thead><tr>{['Student', 'Level', 'Certificate No.', 'Documents', 'Issued', 'Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
              <tbody>
                {cdpCerts.map(c => (
                  <tr key={c._id} style={styles.tr}>
                    <td style={styles.td}><strong>{c.userName}</strong></td>
                    <td style={styles.td}><span style={{ ...styles.badge, ...(c.level === 'gold' ? { background: '#fef3c7', color: '#92400e' } : c.level === 'silver' ? { background: '#e5e7eb', color: '#374151' } : { background: '#dbeafe', color: '#1e40af' }) }}>{c.level}</span></td>
                    <td style={{ ...styles.td, fontSize: '11px', fontFamily: 'monospace' }}><a href={`/cdp/certificate/${encodeURIComponent(c.certificateNumber)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#4338ca', textDecoration: 'underline' }}>{c.certificateNumber}</a></td>
                    <td style={styles.td}>{c.documentTitles?.length || 0}</td>
                    <td style={styles.td}>{new Date(c.issuedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td style={styles.td}>
                      <button onClick={async () => { if (!window.confirm('Regenerate PDF for this certificate?')) return; try { await api.post(`/cdp/admin/certificates/${c._id}/regenerate`); alert('PDF regenerated successfully'); loadCDP(); } catch { alert('Failed to regenerate'); } }} style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '600', color: '#4338ca', cursor: 'pointer' }}>Regenerate PDF</button>
                    </td>
                  </tr>
                ))}
                {cdpCerts.length === 0 && <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No certificates issued yet</td></tr>}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* Feedback Tab */}
      {tab === 'Feedback' && !loading && (
        <div>
          <h2 style={styles.sectionTitle}>All Feedback ({feedbackItems.length})</h2>
          <div style={styles.tableWrap}><table style={styles.table}>
            <thead><tr>{['User', 'Phone', 'Feedback', 'Date'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {feedbackItems.map(fb => (
                <tr key={fb._id} style={styles.tr}>
                  <td style={styles.td}><strong>{fb.userId?.name || '-'}</strong></td>
                  <td style={styles.td}>{fb.userId?.phone || '-'}</td>
                  <td style={{ ...styles.td, maxWidth: '400px', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{fb.text}</td>
                  <td style={styles.td}>{new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                </tr>
              ))}
              {feedbackItems.length === 0 && <tr><td colSpan={4} style={{ ...styles.td, textAlign: 'center', color: '#a0aec0', padding: '32px' }}>No feedback yet</td></tr>}
            </tbody>
          </table></div>
        </div>
      )}

      {/* END TABS */}
    </div>
  );
}

function StatCard({ icon, label, value, color, onClick }) {
  return (
    <div
      style={{ ...styles.statCard, borderLeft: `3px solid ${color}`, cursor: onClick ? 'pointer' : 'default', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
      onClick={onClick}
      onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; } }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <span style={{ fontSize: '28px' }}>{icon}</span>
      <div>
        <p style={styles.statLabel}>{label}</p>
        <p style={{ ...styles.statValue, color }}>{value ?? '-'}</p>
      </div>
    </div>
  );
}

function roleBadgeColor(role) {
  if (role === 'superadmin') return { background: 'var(--danger-bg)', color: 'var(--danger)' };
  if (role === 'admin') return { background: 'var(--warning-bg)', color: 'var(--warning)' };
  return { background: 'var(--border-2)', color: 'var(--text-2)' };
}

function statusBadge(status) {
  if (status === 'ready') return { background: 'var(--success-bg)', color: 'var(--success)' };
  if (status === 'processing') return { background: '#dbeafe', color: '#1e40af' };
  return { background: 'var(--danger-bg)', color: 'var(--danger)' };
}

const styles = {
  page: { maxWidth: '1100px', margin: '0 auto', padding: '28px 20px' },
  title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: '700', color: 'var(--navy)', marginBottom: '20px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid var(--border)', paddingBottom: '0' },
  tab: { padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'var(--text-3)', borderBottom: '2px solid transparent', marginBottom: '-2px', transition: 'all 0.2s', fontFamily: "'DM Sans', sans-serif" },
  tabActive: { color: 'var(--navy)', borderBottomColor: 'var(--navy)' },
  error: { background: 'var(--danger-bg)', border: '1px solid rgba(185,28,28,0.2)', color: 'var(--danger)', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '16px' },
  success: { background: 'var(--success-bg)', border: '1px solid rgba(26,102,64,0.2)', color: 'var(--success)', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '16px' },
  loading: { color: 'var(--text-3)', padding: '20px', textAlign: 'center' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' },
  statCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '18px', display: 'flex', alignItems: 'center', gap: '14px' },
  statLabel: { fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: '600' },
  statValue: { fontSize: '26px', fontWeight: '800' },
  tableWrap: { background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '11px 14px', background: 'var(--bg)', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--border-2)' },
  td: { padding: '13px 14px', fontSize: '13px', color: 'var(--text)' },
  badge: { padding: '3px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' },
  select: { border: '1px solid var(--border)', borderRadius: '6px', padding: '5px 8px', fontSize: '12px', cursor: 'pointer', outline: 'none', background: '#fff', color: 'var(--text)', fontFamily: "'DM Sans', sans-serif" },
  uploadCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '22px', marginBottom: '18px' },
  cardTitle: { fontSize: '14px', fontWeight: '700', color: 'var(--navy)', marginBottom: '14px' },
  uploadForm: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' },
  fileInput: { border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', cursor: 'pointer', background: 'var(--bg)' },
  uploadBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  hint: { fontSize: '12px', color: 'var(--text-3)', lineHeight: '1.6' },
  activateBtn: { background: 'var(--success)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deactivateBtn: { background: '#fff', color: 'var(--warning)', border: '1px solid rgba(146,64,14,0.3)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deleteBtn: { background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid rgba(185,28,28,0.2)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  viewBtn: { background: 'var(--gold-light)', color: 'var(--navy)', border: '1px solid var(--gold-mid)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' },
  disableBtn: { background: '#fff', color: 'var(--danger)', border: '1px solid rgba(185,28,28,0.3)', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif" },
  enableBtn: { background: 'var(--success)', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif" },
  pagination: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px', flexWrap: 'wrap' },
  pageBtn: { background: '#fff', border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', color: 'var(--navy)', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  pageNumbers: { display: 'flex', gap: '4px' },
  pageNumBtn: { width: '32px', height: '32px', border: '1px solid var(--border)', borderRadius: '8px', background: '#fff', fontSize: '12px', fontWeight: '600', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" },
  pageNumActive: { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' },
  pageInfo: { fontSize: '12px', color: 'var(--text-3)', marginLeft: 'auto' },
  chatDashBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' },
  input: { width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
};
